import React, { useState, useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { login } from "../features/auth/authSlice";
import CaptchaVerification from "./CaptchaVerification";
import AuthCard from "./auth/AuthCard";
import Notice from "./ui/Notice";
import { Button, Field, Input } from "../designSystem";
import {
  checkPasswordMessage,
  goodPassword,
  setPodcastSession,
  setToken,
  setUser,
  validateEmail,
  translateAuthError,
} from "../utils";
import { getFullApiUrl, podcastId } from "../config";

// Get or generate device ID for tracking
const getDeviceId = () => {
  let deviceId = localStorage.getItem("deviceId");
  if (!deviceId) {
    // Generate a unique device ID
    deviceId = `web-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    localStorage.setItem("deviceId", deviceId);
  }
  return deviceId;
};

export default function SignIn() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [message, setMessage] = useState("");
  const [step, setStep] = useState(1); // 1: email, 2: password/verification
  const [authMethod, setAuthMethod] = useState(""); // password or verification
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [remainingTime, setRemainingTime] = useState(0);
  const [captchaConfig, setCaptchaConfig] = useState(null);
  const [showCaptcha, setShowCaptcha] = useState(false);
  const [pendingAction, setPendingAction] = useState(null); // For CAPTCHA
  const timerRef = useRef(null);

  // Fetch CAPTCHA config on mount
  useEffect(() => {
    fetchCaptchaConfig();
  }, []);

  // Timer effect for code resend
  useEffect(() => {
    if (remainingTime > 0) {
      timerRef.current = setTimeout(() => {
        setRemainingTime(remainingTime - 1);
      }, 1000);
    }
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [remainingTime]);

  const fetchCaptchaConfig = async () => {
    try {
      const response = await axios.get(
        getFullApiUrl("/api/v1/auth/captcha-config")
      );
      setCaptchaConfig(response.data);
    } catch (err) {
      console.error("Failed to fetch CAPTCHA config:", err);
    }
  };

  const handleCaptchaSuccess = async (verifyParam) => {
    setShowCaptcha(false);

    // Execute the pending action with CAPTCHA param
    if (pendingAction === "sendCode") {
      await sendVerificationCode(email, verifyParam);
    } else if (pendingAction === "verifyCode") {
      await verifyCodeWithCaptcha(verifyParam);
    }
    setPendingAction(null);
  };

  const handleCaptchaError = (error) => {
    setShowCaptcha(false);
    setPendingAction(null);

    // Fallback to password login when CAPTCHA fails
    if (pendingAction === "sendCode") {
      setStep(2);
      setAuthMethod("password");
      setMessage("安全验证失败，请使用密码登录");
    } else {
      setMessage("安全验证失败，请重试");
    }
  };

  const sendVerificationCode = async (userEmail, captchaParam) => {
    setIsLoading(true);
    try {
      const payload = {
        email: userEmail,
        podcast_id: podcastId,
        device_id: getDeviceId(), // Get device ID for tracking
      };
      if (captchaParam) {
        payload.captcha_verify_param = captchaParam;
      }

      const response = await axios.post(
        getFullApiUrl("/api/v1/auth/send-login-code"),
        payload
      );

      if (response.status === 200) {
        setStep(2);
        setAuthMethod("verification");
        setRemainingTime(300); // 5 minutes (updated per security spec)
        setMessage("");
      }
    } catch (err) {
      console.error("Failed to send verification code:", err);

      // Use the translation helper
      const translated = translateAuthError(
        err.response?.data?.error || err.message,
        err
      );

      if (translated.switchToPassword) {
        // Daily limit exceeded - auto switch to password
        setStep(2);
        setAuthMethod("password");
        setMessage(translated.message);
      } else if (
        translated.captchaRequired ||
        err.response?.data?.captcha_required
      ) {
        // CAPTCHA required
        if (
          captchaConfig?.enabled &&
          captchaConfig?.app_key &&
          captchaConfig?.scene_id
        ) {
          setPendingAction("sendCode");
          setShowCaptcha(true);
        } else {
          // CAPTCHA not available - fallback to password
          setStep(2);
          setAuthMethod("password");
          setMessage("安全验证不可用，请使用密码登录");
        }
      } else {
        setMessage(translated.message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const verifyCode = async (code) => {
    const codeToVerify = code || verificationCode;
    if (codeToVerify.length !== 6) return;

    setIsLoading(true);
    setMessage("");
    try {
      const response = await axios.post(
        getFullApiUrl("/api/v1/auth/verify-login-code"),
        {
          email: email,
          verification_code: codeToVerify,
          podcast_id: podcastId,
          device_id: getDeviceId(), // Include device ID for tracking
        }
      );

      if (response.status === 200 && response.data.token) {
        setToken(response.data.token);
        if (response.data.podcast_session) {
          setPodcastSession(response.data.podcast_session);
        }
        setUser(JSON.stringify(response.data.user));
        dispatch(
          login({
            user: response.data.user,
            token: response.data.token,
          })
        );
        navigate("/");
      }
    } catch (err) {
      console.error("Verification failed:", err);

      // Use translation helper
      const translated = translateAuthError(
        err.response?.data?.error || err.message,
        err
      );

      // Check for CAPTCHA requirement
      if (translated.captchaRequired || err.response?.data?.captcha_required) {
        // CAPTCHA required
        if (
          captchaConfig?.enabled &&
          captchaConfig?.app_key &&
          captchaConfig?.scene_id
        ) {
          setPendingAction("verifyCode");
          setShowCaptcha(true);
        } else {
          setMessage("需要安全验证但不可用，请使用密码登录");
          setStep(2);
          setAuthMethod("password");
        }
      } else if (err.response?.status === 429) {
        // Rate limit from backend - just show the message
        setMessage(translated.message);
      } else {
        // Other errors
        const errorMsg =
          translated.message || err.response?.data?.error || "验证码错误";
        if (errorMsg.includes("过期") || errorMsg.includes("expired")) {
          setMessage("验证码已过期，请重新获取");
          setRemainingTime(0); // Allow immediate resend
        } else {
          setMessage(errorMsg);
        }
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Add new function for CAPTCHA-protected verification
  const verifyCodeWithCaptcha = async (captchaParam) => {
    setIsLoading(true);
    try {
      const response = await axios.post(
        getFullApiUrl("/api/v1/auth/verify-login-code"),
        {
          email: email,
          verification_code: verificationCode,
          podcast_id: podcastId,
          device_id: getDeviceId(),
          captcha_verify_param: captchaParam, // Include CAPTCHA param
        }
      );

      if (response.status === 200 && response.data.token) {
        setToken(response.data.token);
        if (response.data.podcast_session) {
          setPodcastSession(response.data.podcast_session);
        }
        setUser(JSON.stringify(response.data.user));
        dispatch(
          login({
            user: response.data.user,
            token: response.data.token,
          })
        );
        navigate("/");
      }
    } catch (err) {
      // Handle error same as verifyCode
      console.error("Verification with CAPTCHA failed:", err);
      const translated = translateAuthError(
        err.response?.data?.error || err.message,
        err
      );
      setMessage(translated.message);
    } finally {
      setIsLoading(false);
    }
  };

  const loginWithPassword = async () => {
    if (!validateEmail(email)) {
      setMessage("请输入有效的邮箱地址");
      return;
    }
    if (checkPasswordMessage(password) !== goodPassword) {
      setMessage("密码格式不正确");
      return;
    }

    setIsLoading(true);
    try {
      const response = await axios.post(getFullApiUrl("/api/v1/auth/login"), {
        email: email,
        password: password,
        podcast_id: podcastId,
      });
      if (response.status === 200 && response.data.token) {
        setToken(response.data.token);
        if (response.data.podcast_session) {
          setPodcastSession(response.data.podcast_session);
        }
        setUser(JSON.stringify(response.data.user));
        dispatch(
          login({
            user: response.data.user,
            token: response.data.token,
          })
        );
        navigate("/");
      }
    } catch (err) {
      console.error("Login failed:", err);
      const translated = translateAuthError(
        err.response?.data?.error || "登录失败",
        err
      );

      if (translated.message.includes("尚未注册")) {
        setMessage(
          "该邮箱尚未注册边角聊，请在 https://leftovertalk.com 完成注册"
        );
      } else {
        setMessage(translated.message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleEmailSubmit = async () => {
    if (!validateEmail(email)) {
      setMessage("请输入有效的邮箱地址");
      return;
    }
    // Always try to send verification code first
    // CAPTCHA will be shown only if server requires it
    await sendVerificationCode(email);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (step === 1) {
      await handleEmailSubmit();
    } else if (authMethod === "password") {
      await loginWithPassword();
    } else if (authMethod === "verification") {
      await verifyCode();
    }
  };

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes.toString().padStart(2, "0")}:${remainingSeconds
      .toString()
      .padStart(2, "0")}`;
  };

  const handleForgotPassword = () => {
    navigate("/forgot", { state: { email: email } });
  };

  const handleBackToEmail = () => {
    setStep(1);
    setAuthMethod("");
    setPassword("");
    setVerificationCode("");
    setMessage("");
    setRemainingTime(0);
  };

  const submitDisabled =
    isLoading ||
    (step === 1 && !validateEmail(email)) ||
    (step === 2 && authMethod === "password" && !password) ||
    (step === 2 &&
      authMethod === "verification" &&
      verificationCode.length !== 6);

  return (
    <AuthCard
      title={
        step === 1
          ? "登录"
          : authMethod === "verification"
          ? "验证码登录"
          : "密码登录"
      }
      subtitle={step === 1 ? "输入邮箱，我们将向您发送登录验证码。" : null}
    >
      <form onSubmit={handleSubmit} noValidate style={{ display: "grid", gap: 16 }}>
        {step === 1 ? (
          <>
            <Field label="邮箱" htmlFor="email">
              <Input
                id="email"
                type="email"
                icon="mail"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                autoFocus
                disabled={showCaptcha}
              />
            </Field>

            {showCaptcha && captchaConfig?.enabled && (
              <CaptchaVerification
                appKey={captchaConfig.app_key}
                sceneId={captchaConfig.scene_id}
                onSuccess={handleCaptchaSuccess}
                onError={handleCaptchaError}
                onCancel={() => {
                  setShowCaptcha(false);
                }}
                embedded={true}
              />
            )}
          </>
        ) : (
          <>
            <div className="lt-auth-email">{email}</div>

            {authMethod === "password" ? (
              <Field label="密码" htmlFor="password">
                <Input
                  id="password"
                  name="password"
                  type="password"
                  icon="key"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  autoFocus
                />
              </Field>
            ) : (
              <Field
                label="6 位验证码"
                htmlFor="verificationCode"
                hint="验证码已发送至您的邮箱。若该邮箱尚未注册，请按照邮件指引完成注册；如未收到，请检查垃圾邮件。"
              >
                <Input
                  id="verificationCode"
                  name="verificationCode"
                  icon="pin"
                  inputMode="numeric"
                  value={verificationCode}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, "").slice(0, 6);
                    setVerificationCode(value);
                    // Auto-submit removed to prevent race conditions with CAPTCHA
                  }}
                  autoComplete="one-time-code"
                  autoFocus
                  maxLength={6}
                  style={{ flex: 1 }}
                />
              </Field>
            )}

            {/* CAPTCHA for Step 2 (verification code mode) */}
            {showCaptcha &&
              captchaConfig?.enabled &&
              authMethod === "verification" && (
                <CaptchaVerification
                  appKey={captchaConfig.app_key}
                  sceneId={captchaConfig.scene_id}
                  onSuccess={handleCaptchaSuccess}
                  onError={handleCaptchaError}
                  onCancel={() => {
                    setShowCaptcha(false);
                  }}
                  embedded={true}
                />
              )}
          </>
        )}

        <Notice variant="error">{message}</Notice>

        {!showCaptcha && (
          <Button type="submit" size="lg" fullWidth loading={isLoading} disabled={submitDisabled}>
            {step === 1 ? "继续" : "登录"}
          </Button>
        )}

        {step === 1 ? (
          <div className="lt-auth-links">
            <Button type="button" variant="ghost" size="sm" onClick={handleForgotPassword}>
              忘记密码？
            </Button>
            <Button type="button" variant="ghost" size="sm" onClick={() => navigate("/signup")}>
              注册
            </Button>
          </div>
        ) : (
          <>
            {authMethod === "password" ? (
              <div className="lt-auth-links">
                <Button type="button" variant="ghost" size="sm" onClick={handleForgotPassword}>
                  忘记密码？
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={async () => {
                    await sendVerificationCode(email);
                  }}
                >
                  使用验证码登录
                </Button>
              </div>
            ) : (
              <div className="lt-auth-links">
                {remainingTime > 0 ? (
                  <span
                    style={{
                      fontSize: "var(--text-sm)",
                      color: "var(--text-3)",
                      fontFamily: "var(--font-mono)",
                    }}
                  >
                    重新发送 ({formatTime(remainingTime)})
                  </span>
                ) : (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={async () => {
                      await sendVerificationCode(email);
                    }}
                  >
                    重新发送验证码
                  </Button>
                )}
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setAuthMethod("password");
                    setVerificationCode("");
                    setMessage("");
                  }}
                >
                  使用密码登录
                </Button>
              </div>
            )}
            <Button
              type="button"
              variant="ghost"
              size="sm"
              icon="arrow_back"
              style={{ justifySelf: "center" }}
              onClick={handleBackToEmail}
            >
              返回
            </Button>
          </>
        )}
      </form>
    </AuthCard>
  );
}
